import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { PageLoader } from '../components/page-loader';
import { ConfigurationLayout } from '../layouts/configuration-layout';
import { MainLayout } from '../layouts/main-layout';
import { Pages } from './pages';

// Lazy load page components
const HomePage = lazy(() => import('../pages/home/home-page').then(m => ({ default: m.HomePage })));
const PositionsPage = lazy(() => import('../pages/positions/positions-page').then(m => ({ default: m.PositionsPage })));
const TasksPage = lazy(() => import('../pages/tasks/tasks-page').then(m => ({ default: m.TasksPage })));
const BenchmarksPage = lazy(() =>
  import('../pages/benchmarks/benchmarks-page').then(m => ({ default: m.BenchmarksPage })),
);
const JobDescriptionsPage = lazy(() =>
  import('../pages/job-descriptions/job-descriptions-page').then(m => ({ default: m.JobDescriptionsPage })),
);

// Configuration pages
const ConfigurationLandingPage = lazy(() =>
  import('../pages/configuration/configuration-landing-page').then(m => ({ default: m.ConfigurationLandingPage })),
);
const ApiConfigurationPage = lazy(() =>
  import('../pages/configuration/api-configuration/api-configuration-page').then(m => ({
    default: m.ApiConfigurationPage,
  })),
);
const AiConfigurationPage = lazy(() =>
  import('../pages/configuration/api-configuration/ai/ai-configuration-page.tsx').then(m => ({
    default: m.AiConfigurationPage,
  })),
);
const SfConfigurationPage = lazy(() =>
  import('../pages/configuration/api-configuration/sf/sf-configuration-page.tsx').then(m => ({
    default: m.SfConfigurationPage,
  })),
);
const AiPromptsPage = lazy(() =>
  import('../pages/configuration/ai-prompts/ai-prompts-page').then(m => ({ default: m.AiPromptsPage })),
);
const PromptPlaygroundPage = lazy(() =>
  import('../pages/configuration/ai-prompts/prompt-playground/prompt-playground-page.tsx').then(m => ({
    default: m.PromptPlaygroundPage,
  })),
);
const FieldMappingsPage = lazy(() =>
  import('../pages/configuration/field-mappings/field-mappings-page').then(m => ({ default: m.FieldMappingsPage })),
);
const CountryCodesPage = lazy(() =>
  import('../pages/configuration/country-codes/country-codes-page').then(m => ({ default: m.CountryCodesPage })),
);

// JobClassification pages
const JobClassificationPage = lazy(() =>
  import('../pages/configuration/job-classification/job-classification-page').then(m => ({
    default: m.JobClassificationPage,
  })),
);
const JobLevelsPage = lazy(() =>
  import('../pages/configuration/job-classification/job-levels/job-levels-page').then(m => ({
    default: m.JobLevelsPage,
  })),
);
const JobClassificationCodesPage = lazy(() =>
  import('../pages/configuration/job-classification/job-classification-codes/job-classification-codes-page').then(
    m => ({ default: m.JobClassificationCodesPage }),
  ),
);
const JobFamiliesPage = lazy(() =>
  import('../pages/configuration/job-classification/job-families/job-families-page').then(m => ({
    default: m.JobFamiliesPage,
  })),
);
const SkillTypesPage = lazy(() =>
  import('../pages/configuration/job-classification/skill-types/skill-types-page').then(m => ({
    default: m.SkillTypesPage,
  })),
);
const SkillClustersPage = lazy(() =>
  import('../pages/configuration/job-classification/skill-clusters/skill-clusters-page').then(m => ({
    default: m.SkillClustersPage,
  })),
);
const SkillLevelsPage = lazy(() =>
  import('../pages/configuration/job-classification/skill-levels/skill-levels-page').then(m => ({
    default: m.SkillLevelsPage,
  })),
);
const SkillsPage = lazy(() =>
  import('../pages/configuration/job-classification/skills/skills-page').then(m => ({ default: m.SkillsPage })),
);
const CompetenciesPage = lazy(() =>
  import('../pages/configuration/job-classification/competencies/competencies-page').then(m => ({
    default: m.CompetenciesPage,
  })),
);
const JobFamilyCompetencyPage = lazy(() =>
  import('../pages/configuration/job-classification/mappings/job-family-competency/job-family-competency-page').then(
    m => ({ default: m.JobFamilyCompetencyPage }),
  ),
);
const JobClassificationSkillPage = lazy(() =>
  import(
    '../pages/configuration/job-classification/mappings/job-classification-skill/job-classification-skill-page'
  ).then(m => ({ default: m.JobClassificationSkillPage })),
);

const LazyWrapper = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<PageLoader />}>{children}</Suspense>
);

export const router = createBrowserRouter([
  {
    path: Pages.HOME.path,
    element: <MainLayout />,
    children: [
      {
        index: true,
        element: (
          <LazyWrapper>
            <HomePage />
          </LazyWrapper>
        ),
      },
      {
        path: Pages.POSITIONS.path,
        element: (
          <LazyWrapper>
            <PositionsPage />
          </LazyWrapper>
        ),
      },
      {
        path: Pages.TASKS.path,
        element: (
          <LazyWrapper>
            <TasksPage />
          </LazyWrapper>
        ),
      },
      {
        path: Pages.BENCHMARKS.path,
        element: (
          <LazyWrapper>
            <BenchmarksPage />
          </LazyWrapper>
        ),
      },
      {
        path: Pages.JOB_DESCRIPTIONS.path,
        element: (
          <LazyWrapper>
            <JobDescriptionsPage />
          </LazyWrapper>
        ),
      },
    ],
  },
  {
    path: Pages.CONFIGURATION.link,
    element: <ConfigurationLayout />,
    children: [
      {
        index: true,
        element: (
          <LazyWrapper>
            <ConfigurationLandingPage />
          </LazyWrapper>
        ),
      },
      {
        path: Pages.CONFIGURATION.children.API_CONFIGURATION.path,
        children: [
          {
            index: true,
            element: (
              <LazyWrapper>
                <ApiConfigurationPage />
              </LazyWrapper>
            ),
          },
          {
            path: Pages.CONFIGURATION.children.API_CONFIGURATION.children.AI_CONFIGURATION.path,
            element: (
              <LazyWrapper>
                <AiConfigurationPage />
              </LazyWrapper>
            ),
          },
          {
            path: Pages.CONFIGURATION.children.API_CONFIGURATION.children.SF_CONFIGURATION.path,
            element: (
              <LazyWrapper>
                <SfConfigurationPage />
              </LazyWrapper>
            ),
          },
        ],
      },
      {
        path: Pages.CONFIGURATION.children.AI_PROMPTS.path,
        children: [
          {
            index: true,
            element: (
              <LazyWrapper>
                <AiPromptsPage />
              </LazyWrapper>
            ),
          },
          {
            path: Pages.CONFIGURATION.children.AI_PROMPTS.children.PROMPT_PLAYGROUND.path,
            element: (
              <LazyWrapper>
                <PromptPlaygroundPage />
              </LazyWrapper>
            ),
          },
        ],
      },
      {
        path: Pages.CONFIGURATION.children.FIELD_MAPPINGS.path,
        element: (
          <LazyWrapper>
            <FieldMappingsPage />
          </LazyWrapper>
        ),
      },
      {
        path: Pages.CONFIGURATION.children.COUNTRY_CODES.path,
        element: (
          <LazyWrapper>
            <CountryCodesPage />
          </LazyWrapper>
        ),
      },
      {
        path: Pages.CONFIGURATION.children.JOB_CLASSIFICATION.path,
        children: [
          {
            index: true,
            element: (
              <LazyWrapper>
                <JobClassificationPage />
              </LazyWrapper>
            ),
          },
          {
            path: Pages.CONFIGURATION.children.JOB_CLASSIFICATION.children.JOB_LEVELS.path,
            element: (
              <LazyWrapper>
                <JobLevelsPage />
              </LazyWrapper>
            ),
          },
          {
            path: Pages.CONFIGURATION.children.JOB_CLASSIFICATION.children.JOB_CLASSIFICATION_CODES.path,
            element: (
              <LazyWrapper>
                <JobClassificationCodesPage />
              </LazyWrapper>
            ),
          },
          {
            path: Pages.CONFIGURATION.children.JOB_CLASSIFICATION.children.JOB_FAMILIES.path,
            element: (
              <LazyWrapper>
                <JobFamiliesPage />
              </LazyWrapper>
            ),
          },
          {
            path: Pages.CONFIGURATION.children.JOB_CLASSIFICATION.children.SKILL_TYPES.path,
            element: (
              <LazyWrapper>
                <SkillTypesPage />
              </LazyWrapper>
            ),
          },
          {
            path: Pages.CONFIGURATION.children.JOB_CLASSIFICATION.children.SKILL_CLUSTERS.path,
            element: (
              <LazyWrapper>
                <SkillClustersPage />
              </LazyWrapper>
            ),
          },
          {
            path: Pages.CONFIGURATION.children.JOB_CLASSIFICATION.children.SKILL_LEVELS.path,
            element: (
              <LazyWrapper>
                <SkillLevelsPage />
              </LazyWrapper>
            ),
          },
          {
            path: Pages.CONFIGURATION.children.JOB_CLASSIFICATION.children.SKILLS.path,
            element: (
              <LazyWrapper>
                <SkillsPage />
              </LazyWrapper>
            ),
          },
          {
            path: Pages.CONFIGURATION.children.JOB_CLASSIFICATION.children.COMPETENCIES.path,
            element: (
              <LazyWrapper>
                <CompetenciesPage />
              </LazyWrapper>
            ),
          },
          {
            path: `${Pages.CONFIGURATION.children.JOB_CLASSIFICATION.children.MAPPINGS.path}/${Pages.CONFIGURATION.children.JOB_CLASSIFICATION.children.MAPPINGS.children.JOB_FAMILY_COMPETENCY.path}`,
            element: (
              <LazyWrapper>
                <JobFamilyCompetencyPage />
              </LazyWrapper>
            ),
          },
          {
            path: `${Pages.CONFIGURATION.children.JOB_CLASSIFICATION.children.MAPPINGS.path}/${Pages.CONFIGURATION.children.JOB_CLASSIFICATION.children.MAPPINGS.children.JOB_CLASSIFICATION_SKILL.path}`,
            element: (
              <LazyWrapper>
                <JobClassificationSkillPage />
              </LazyWrapper>
            ),
          },
        ],
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to={Pages.HOME.link} replace />,
  },
]);
